import React, { useCallback, useEffect, useState } from 'react';

import {
  atualizarEtapaExterna,
  atualizarItemDoChecklist,
  atualizarOficinaExterna,
  atualizarRegiaoExterna,
  criarEtapaExterna,
  criarItemDoChecklist,
  criarOficinaExterna,
  criarRegiaoExterna,
  desativarEtapaExterna,
  desativarItemDoChecklist,
  desativarOficinaExterna,
  desativarRegiaoExterna,
  fetchConfigChecklist,
  fetchConfigEtapas,
  fetchConfigOficinas,
  fetchConfigRegioes,
  fetchParametrosOficinaExterna,
  reordenarChecklistDaVisita,
  reordenarEtapasExternas,
  salvarParametrosOficinaExterna,
  dispararAvisoSemVisita,
  type ChecklistVisita,
  type ChecklistVisitaItem,
  type ChecklistVisitaTipo,
  type EtapaExterna,
  type OficinaExterna,
  type ParametrosOficinaExterna,
  type RegiaoExterna,
} from '../../../api/oficinaExternaApi';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import HistoricoAlteracoes from '../../../components/historico-alteracoes/HistoricoAlteracoes';
import Paginacao from '../../../components/paginacao/Paginacao';
// A tabela no padrão do sistema (`TabelaScroll` > `Tabela` > `Item` > `Data`, colunas medidas) —
// o Victor recusou a `<table>` com `admin-table` em 25/09/2026: "tem que ter os formatos de
// tabela da aplicação". O CSS do grid vem do `Recepcao.css`, já importado abaixo.
import TabelaSistema, { type ColunaTabela } from '../../../components/sync-painel/TabelaSistema';
import { notify } from '../../../shared/feedback/notify';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';
import '../../recepcao/pages/Recepcao.css';
import './css/adminSistema.css';

type Aba = 'REGIOES' | 'OFICINAS' | 'ETAPAS' | 'CHECKLIST' | 'PARAMETROS';

const ABAS: Array<{ chave: Aba; rotulo: string }> = [
  { chave: 'REGIOES', rotulo: 'Regiões' },
  { chave: 'OFICINAS', rotulo: 'Oficinas' },
  { chave: 'ETAPAS', rotulo: 'Etapas' },
  { chave: 'CHECKLIST', rotulo: 'Checklist da visita' },
  { chave: 'PARAMETROS', rotulo: 'Parâmetros' },
];

const TIPOS: Array<{ valor: ChecklistVisitaTipo; rotulo: string }> = [
  { valor: 'SIM_NAO', rotulo: 'Sim / Não' },
  { valor: 'ESCOLHA_UNICA', rotulo: 'Escolha única' },
  { valor: 'ESCOLHA_MULTIPLA', rotulo: 'Escolha múltipla' },
  { valor: 'TEXTO', rotulo: 'Texto' },
  { valor: 'NUMERO', rotulo: 'Número' },
  { valor: 'DATA', rotulo: 'Data' },
];

const TIPOS_COM_OPCOES: ChecklistVisitaTipo[] = ['ESCOLHA_UNICA', 'ESCOLHA_MULTIPLA'];

const OFICINAS_POR_PAGINA = 15;

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'O que se configura aqui',
    conteudo: (
      <>
        <p>
          A estrutura sobre a qual o <strong>inspetor</strong> trabalha: as <strong>regiões</strong>{' '}
          e as <strong>oficinas externas</strong> que ele visita, as <strong>etapas</strong> que a
          oficina pode informar, as perguntas do <strong>checklist da visita</strong> e a partir de
          quantos dias sem visita um veículo aparece sinalizado.
        </p>
        <p>
          Enquanto não houver ao menos uma região e uma oficina, a tela do inspetor abre vazia — o
          módulo nasce sem nada cadastrado, de propósito.
        </p>
      </>
    ),
  },
  {
    titulo: 'Por que se desativa em vez de excluir',
    conteudo: (
      <>
        <p>
          Região, oficina, etapa e item do checklist <strong>nunca são apagados</strong>: eles são
          desativados. As visitas já registradas apontam para eles, e apagar destruiria o registro
          do que foi observado — que é justamente o que esta feature existe para guardar.
        </p>
        <p>
          Desativado significa: some das telas de cadastro novo, continua legível no histórico.
        </p>
      </>
    ),
  },
  {
    titulo: 'Como o checklist chega ao celular do inspetor',
    conteudo: (
      <>
        <p>
          O aparelho baixa o checklist junto com os outros dados quando tem rede. Uma mudança feita
          aqui <strong>só chega ao inspetor no próximo download</strong> — ele pode estar na rua,
          sem sinal, com a versão anterior.
        </p>
        <p>
          Por isso, uma pergunta nova marcada como obrigatória <strong>não reprova</strong> uma
          visita preenchida antes da mudança: o sistema sabe qual versão o aparelho tinha, e não
          cobra resposta de uma pergunta que nunca foi exibida a ele.
        </p>
        <p>
          O <strong>tipo</strong> de uma pergunta não pode ser alterado depois de criada: mudá-lo
          reinterpretaria as respostas antigas (um "Sim" viraria texto). Para trocar o tipo,
          desative a pergunta e crie outra.
        </p>
      </>
    ),
  },
  {
    titulo: 'Dias sem visita',
    conteudo: (
      <>
        <p>
          Define a partir de quantos dias um veículo aparece <strong>sinalizado</strong> na
          cobertura e na tela de acompanhamento, e a partir de quando ele gera{' '}
          <strong>aviso</strong>. O veículo nunca visitado conta desde a entrada na oficina.
        </p>
        <p>
          O aviso vai ao sino do <strong>Chefe de Oficina</strong>, do{' '}
          <strong>setor de Eventos</strong> e dos administradores, todo dia às 8h. Sai{' '}
          <strong>uma vez por veículo</strong>: só volta a sair depois de uma visita nova, se o
          veículo passar do limite de novo. Por isso <strong>Avisar agora</strong> pode ser usado
          sem medo de repetir aviso.
        </p>
      </>
    ),
  },
];

/**
 * Administração do Sistema → Oficina Externa (at-114, ADR 0059) — ADMIN e TI.
 *
 * Casco da Recepção + classes `admin-*` (`13-catalogo-de-ui.md`): nenhum CSS novo nesta página.
 */
const OficinaExternaConfigPage: React.FC = () => {
  const [aba, setAba] = useState<Aba>('REGIOES');
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [historicoAberto, setHistoricoAberto] = useState(false);

  const [regioes, setRegioes] = useState<RegiaoExterna[]>([]);
  const [oficinas, setOficinas] = useState<OficinaExterna[]>([]);
  const [etapas, setEtapas] = useState<EtapaExterna[]>([]);
  const [checklist, setChecklist] = useState<ChecklistVisita | null>(null);
  const [parametros, setParametros] = useState<ParametrosOficinaExterna | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const [novaRegiao, setNovaRegiao] = useState('');
  const [novaEtapa, setNovaEtapa] = useState('');
  const [buscaOficina, setBuscaOficina] = useState('');
  const [paginaOficinas, setPaginaOficinas] = useState(1);
  const [novaOficina, setNovaOficina] = useState({
    regiaoId: '',
    nome: '',
    endereco: '',
    telefone: '',
    contato: '',
    observacoes: '',
  });
  const [novoItem, setNovoItem] = useState<{
    rotulo: string;
    tipo: ChecklistVisitaTipo;
    obrigatorio: boolean;
    opcoes: string;
  }>({ rotulo: '', tipo: 'SIM_NAO', obrigatorio: false, opcoes: '' });
  const [diasAlerta, setDiasAlerta] = useState(3);

  const carregar = useCallback(async () => {
    try {
      const [rs, os, es, cl, pr] = await Promise.all([
        fetchConfigRegioes(),
        fetchConfigOficinas(),
        fetchConfigEtapas(),
        fetchConfigChecklist(),
        fetchParametrosOficinaExterna(),
      ]);
      setRegioes(rs);
      setOficinas(os);
      setEtapas(es);
      setChecklist(cl);
      setParametros(pr);
      setDiasAlerta(pr.diasSemVisitaAlerta);
      setErro(null);
    } catch (error) {
      // Estado seguro E sinal visível (at-60): sem isso, listas vazias seriam lidas como
      // "não há nada cadastrado" e o ADMIN recriaria o que já existe.
      setErro(
        'Não foi possível carregar a configuração da Oficina Externa. Recarregue a página; se ' +
          'persistir, acione a TI. Não cadastre nada enquanto este aviso estiver na tela: o que ' +
          'já existe pode não estar sendo exibido.',
      );
      notify.errorFrom(error, 'Não foi possível carregar a configuração da Oficina Externa.');
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const executar = async (acao: () => Promise<unknown>, sucesso: string, falha: string) => {
    setSalvando(true);
    try {
      await acao();
      await carregar();
      notify.success(sucesso);
    } catch (error) {
      // Nunca toast de sucesso para o que falhou — a mensagem do backend explica o que impediu
      // (ex: região com oficinas ativas, oficina com veículos sem saída).
      notify.errorFrom(error, falha);
    } finally {
      setSalvando(false);
    }
  };

  /**
   * at-132: o resultado do disparo é dito por extenso — "nenhum veículo" aqui é estado legítimo
   * (todos já avisados, ou todos visitados), e só aparece quando a chamada RESPONDEU.
   */
  const avisarAgora = async () => {
    setSalvando(true);
    try {
      const { veiculos } = await dispararAvisoSemVisita();
      if (veiculos === 0) {
        notify.success('Nenhum veículo novo passou do limite: não havia aviso a enviar.');
      } else {
        notify.success(
          `Aviso enviado sobre ${veiculos} ${veiculos === 1 ? 'veículo' : 'veículos'} sem visita.`,
        );
      }
    } catch (error) {
      notify.errorFrom(error, 'Não foi possível enviar o aviso de veículos sem visita.');
    } finally {
      setSalvando(false);
    }
  };

  const oficinasFiltradas = oficinas.filter((o) =>
    buscaOficina.trim()
      ? `${o.nome} ${o.regiao?.nome ?? ''}`.toLowerCase().includes(buscaOficina.trim().toLowerCase())
      : true,
  );
  const totalPaginasOficinas = Math.max(
    1,
    Math.ceil(oficinasFiltradas.length / OFICINAS_POR_PAGINA),
  );
  const oficinasDaPagina = oficinasFiltradas.slice(
    (paginaOficinas - 1) * OFICINAS_POR_PAGINA,
    paginaOficinas * OFICINAS_POR_PAGINA,
  );

  /**
   * A lista de ids com o item movido da posição `indice` para `destino` (as duas 0-based). O
   * backend recebe a lista COMPLETA e renumera — é declarativo, como o `reorder` do checklist.
   */
  const moverPara = (lista: Array<{ id: string }>, indice: number, destino: number): string[] => {
    const ids = lista.map((i) => i.id);
    if (destino < 0 || destino >= ids.length || destino === indice) return ids;
    const [id] = ids.splice(indice, 1);
    ids.splice(destino, 0, id);
    return ids;
  };

  const itensAtivos = (checklist?.itens ?? []).filter((i) => i.ativo);

  /** Situação como na aba Especialidades: texto quando ativa, selo cinza quando não. */
  const situacaoTexto = (ativa: boolean | undefined) => (ativa ? 'Ativa' : 'Desativada');
  const situacao = (ativa: boolean | undefined) =>
    ativa ? 'Ativa' : <span className="admin-badge-inactive">Desativada</span>;

  /** O botão Desativar/Reativar de uma linha — mesmo desenho nas quatro tabelas. */
  const botaoAtivar = (
    ativa: boolean | undefined,
    desativar: () => Promise<unknown>,
    reativar: () => Promise<unknown>,
    nome: string,
  ) => (
    <div className="admin-table-acoes">
      <button
        type="button"
        className={`admin-btn ${ativa ? 'admin-btn-danger-outline' : 'admin-btn-secondary'}`}
        disabled={salvando}
        onClick={() =>
          executar(
            ativa ? desativar : reativar,
            ativa ? `${nome} desativada.` : `${nome} reativada.`,
            `Não foi possível alterar a ${nome.toLowerCase()}.`,
          )
        }
      >
        {ativa ? 'Desativar' : 'Reativar'}
      </button>
    </div>
  );

  /**
   * A ordem como NÚMERO (pedido do Victor, 25/09/2026: "mudar ordem assim não dá, tem que ser
   * via número"). Um seletor com as posições 1..N do conjunto ativo; escolher outra posição move
   * o item para lá e o backend renumera o resto. Só o item ativo tem ordem — o desativado saiu
   * da sequência, e a célula fica vazia em vez de exibir um número que não vale.
   *
   * `<select>` e não `<input type="number">`: com N posições fechadas, digitar 0, 99 ou 2,5 são
   * erros que o seletor torna impossíveis, e o backend recusaria a lista com mensagem genérica.
   */
  const seletorDeOrdem = (
    ativo: boolean | undefined,
    indiceAtivo: number,
    total: number,
    moverParaIndice: (destino: number) => Promise<unknown>,
    falha: string,
  ) =>
    ativo ? (
      <select
        className="admin-select"
        style={{ width: 72, padding: '4px 8px' }}
        value={indiceAtivo + 1}
        disabled={salvando || total < 2}
        aria-label="Posição na ordem"
        title="Escolha a posição; os demais são renumerados"
        onChange={(e) =>
          executar(() => moverParaIndice(Number(e.target.value) - 1), 'Ordem atualizada.', falha)
        }
      >
        {Array.from({ length: total }, (_, i) => (
          <option key={i} value={i + 1}>
            {i + 1}
          </option>
        ))}
      </select>
    ) : null;

  const colunasRegioes: ColunaTabela<RegiaoExterna>[] = [
    { titulo: 'Região', largura: 240, texto: (r) => r.nome },
    { titulo: 'Oficinas', largura: 110, texto: (r) => String(r.totalOficinas ?? 0) },
    {
      titulo: 'Situação',
      largura: 130,
      texto: (r) => situacaoTexto(r.ativa),
      render: (r) => situacao(r.ativa),
    },
    {
      titulo: 'Ações',
      largura: 130,
      texto: () => '',
      render: (r) =>
        botaoAtivar(
          r.ativa,
          () => desativarRegiaoExterna(r.id),
          () => atualizarRegiaoExterna(r.id, { ativa: true }),
          'Região',
        ),
    },
  ];

  const colunasOficinas: ColunaTabela<OficinaExterna>[] = [
    { titulo: 'Oficina', largura: 220, texto: (o) => o.nome },
    { titulo: 'Região', largura: 160, texto: (o) => o.regiao?.nome ?? '-' },
    {
      titulo: 'Contato',
      largura: 180,
      texto: (o) => `${o.contato ?? ''} ${o.telefone ?? ''}`.trim() || '-',
    },
    { titulo: 'Veículos lá', largura: 110, texto: (o) => String(o.atendimentosAtivos ?? 0) },
    {
      titulo: 'Situação',
      largura: 130,
      texto: (o) => situacaoTexto(o.ativa),
      render: (o) => situacao(o.ativa),
    },
    {
      titulo: 'Ações',
      largura: 130,
      texto: () => '',
      render: (o) =>
        botaoAtivar(
          o.ativa,
          () => desativarOficinaExterna(o.id),
          () => atualizarOficinaExterna(o.id, { ativa: true }),
          'Oficina',
        ),
    },
  ];

  const etapasAtivas = etapas.filter((x) => x.ativa);
  const colunasEtapas: ColunaTabela<EtapaExterna>[] = [
    { titulo: 'Etapa', largura: 240, texto: (e) => e.nome },
    {
      titulo: 'Situação',
      largura: 130,
      texto: (e) => situacaoTexto(e.ativa),
      render: (e) => situacao(e.ativa),
    },
    {
      titulo: 'Ordem',
      largura: 90,
      texto: () => '',
      render: (e) => {
        const indiceAtivo = etapasAtivas.findIndex((x) => x.id === e.id);
        return seletorDeOrdem(
          e.ativa,
          indiceAtivo,
          etapasAtivas.length,
          (destino) => reordenarEtapasExternas(moverPara(etapasAtivas, indiceAtivo, destino)),
          'Não foi possível reordenar as etapas.',
        );
      },
    },
    {
      titulo: 'Ações',
      largura: 130,
      texto: () => '',
      render: (e) =>
        botaoAtivar(
          e.ativa,
          () => desativarEtapaExterna(e.id),
          () => atualizarEtapaExterna(e.id, { ativa: true }),
          'Etapa',
        ),
    },
  ];

  const colunasChecklist: ColunaTabela<ChecklistVisitaItem>[] = [
    { titulo: 'Pergunta', largura: 280, texto: (i) => i.rotulo },
    {
      titulo: 'Tipo',
      largura: 140,
      texto: (i) => TIPOS.find((t) => t.valor === i.tipo)?.rotulo ?? i.tipo,
    },
    {
      titulo: 'Obrigatória',
      largura: 110,
      texto: (i) => (i.obrigatorio ? 'Sim' : 'Não'),
      // A célula é o interruptor: clicar alterna. O rótulo diz o estado atual e o `title` diz
      // o que o clique faz — sem isso, "Sim" num botão se lê como "confirmar".
      render: (i) => (
        <label className="admin-checkbox-label">
          <input
            type="checkbox"
            checked={i.obrigatorio}
            disabled={salvando || !i.ativo}
            title={i.obrigatorio ? 'Tornar opcional' : 'Tornar obrigatória'}
            onChange={() =>
              executar(
                () => atualizarItemDoChecklist(i.id, { obrigatorio: !i.obrigatorio }),
                'Pergunta atualizada.',
                'Não foi possível atualizar a pergunta.',
              )
            }
          />
          {i.obrigatorio ? 'Sim' : 'Não'}
        </label>
      ),
    },
    {
      titulo: 'Situação',
      largura: 130,
      texto: (i) => situacaoTexto(i.ativo),
      render: (i) => situacao(i.ativo),
    },
    {
      titulo: 'Ordem',
      largura: 90,
      texto: () => '',
      render: (i) => {
        const indiceAtivo = itensAtivos.findIndex((x) => x.id === i.id);
        return seletorDeOrdem(
          i.ativo,
          indiceAtivo,
          itensAtivos.length,
          (destino) => reordenarChecklistDaVisita(moverPara(itensAtivos, indiceAtivo, destino)),
          'Não foi possível reordenar as perguntas.',
        );
      },
    },
    {
      titulo: 'Ações',
      largura: 130,
      texto: () => '',
      render: (i) =>
        botaoAtivar(
          i.ativo,
          () => desativarItemDoChecklist(i.id),
          () => atualizarItemDoChecklist(i.id, { ativo: true }),
          'Pergunta',
        ),
    },
  ];

  return (
    <div className="ConteDo recepcao-conte-do">
      {erro && (
        <div className="erro-de-tela" role="alert">
          {erro}
        </div>
      )}

      <div className="recepcao-header-row">
        <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Oficina Externa
          <button
            type="button"
            className="help-circle-btn"
            title="Como Funciona"
            onClick={() => setIsHelpOpen(true)}
          >
            ?
          </button>
        </div>
      </div>

      <div className="recepcao-tabs" role="tablist">
        {ABAS.map((a) => (
          <button
            key={a.chave}
            type="button"
            role="tab"
            aria-selected={aba === a.chave}
            className={`recepcao-tab ${aba === a.chave ? 'active' : ''}`}
            onClick={() => setAba(a.chave)}
          >
            {a.rotulo}
          </button>
        ))}
      </div>

      {aba === 'REGIOES' && (
        <div className="admin-card">
          <h3 className="admin-card-title">Regiões</h3>
          <p className="admin-hint">
            Agrupam as oficinas na árvore que o inspetor navega. Desativar uma região exige que
            nenhuma oficina ativa esteja nela.
          </p>

          {/* `admin-form-grid` (campos lado a lado, botão alinhado à base) — o molde da aba
              Especialidades. A primeira versão punha input e botão soltos num `admin-input-group`,
              que é coluna: o botão esticava até a borda do cartão. */}
          <div className="admin-form-grid">
            <div className="admin-input-group">
              <label className="admin-label" htmlFor="oe-nova-regiao">
                Nome da região
              </label>
              <input
                id="oe-nova-regiao"
                className="admin-input"
                value={novaRegiao}
                onChange={(e) => setNovaRegiao(e.target.value)}
                placeholder="Ex: Araruama"
              />
            </div>
            <button
              type="button"
              className="admin-btn admin-btn-primary"
              disabled={salvando || !novaRegiao.trim()}
              onClick={() =>
                executar(
                  async () => {
                    await criarRegiaoExterna(novaRegiao.trim());
                    setNovaRegiao('');
                  },
                  'Região criada.',
                  'Não foi possível criar a região.',
                )
              }
            >
              Adicionar
            </button>
          </div>

          <div style={{ marginTop: 20 }}>
            <TabelaSistema
              colunas={colunasRegioes}
              itens={regioes}
              chave={(r) => r.id}
              vazio="Nenhuma região cadastrada ainda."
            />
          </div>
        </div>
      )}

      {aba === 'OFICINAS' && (
        <div className="admin-card">
          <h3 className="admin-card-title">Oficinas externas</h3>
          <p className="admin-hint">
            As oficinas de terceiros que os inspetores visitam. Desativar exige que nenhum veículo
            esteja sem saída registrada nela.
          </p>

          <div className="admin-form-grid">
            <div className="admin-input-group fixed-width">
              <label className="admin-label" htmlFor="oe-oficina-regiao">
                Região
              </label>
              <select
                id="oe-oficina-regiao"
                className="admin-select"
                value={novaOficina.regiaoId}
                onChange={(e) => setNovaOficina({ ...novaOficina, regiaoId: e.target.value })}
              >
                <option value="">Selecione...</option>
                {regioes
                  .filter((r) => r.ativa)
                  .map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.nome}
                    </option>
                  ))}
              </select>
            </div>
            <div className="admin-input-group">
              <label className="admin-label" htmlFor="oe-oficina-nome">
                Nome da oficina
              </label>
              <input
                id="oe-oficina-nome"
                className="admin-input"
                value={novaOficina.nome}
                onChange={(e) => setNovaOficina({ ...novaOficina, nome: e.target.value })}
                placeholder="Ex: Oficina do Carlos"
              />
            </div>
            <div className="admin-input-group">
              <label className="admin-label" htmlFor="oe-oficina-contato">
                Contato (opcional)
              </label>
              <input
                id="oe-oficina-contato"
                className="admin-input"
                value={novaOficina.contato}
                onChange={(e) => setNovaOficina({ ...novaOficina, contato: e.target.value })}
                placeholder="Nome de quem atende"
              />
            </div>
            <div className="admin-input-group fixed-width">
              <label className="admin-label" htmlFor="oe-oficina-telefone">
                Telefone (opcional)
              </label>
              <input
                id="oe-oficina-telefone"
                className="admin-input"
                value={novaOficina.telefone}
                onChange={(e) => setNovaOficina({ ...novaOficina, telefone: e.target.value })}
                placeholder="(21) 99999-9999"
              />
            </div>
            <div className="admin-input-group">
              <label className="admin-label" htmlFor="oe-oficina-endereco">
                Endereço (opcional)
              </label>
              <input
                id="oe-oficina-endereco"
                className="admin-input"
                value={novaOficina.endereco}
                onChange={(e) => setNovaOficina({ ...novaOficina, endereco: e.target.value })}
                placeholder="Rua, número, bairro"
              />
            </div>
            <button
              type="button"
              className="admin-btn admin-btn-primary"
              disabled={salvando || !novaOficina.regiaoId || !novaOficina.nome.trim()}
              onClick={() =>
                executar(
                  async () => {
                    await criarOficinaExterna({
                      regiaoId: novaOficina.regiaoId,
                      nome: novaOficina.nome.trim(),
                      endereco: novaOficina.endereco.trim() || undefined,
                      telefone: novaOficina.telefone.trim() || undefined,
                      contato: novaOficina.contato.trim() || undefined,
                      observacoes: novaOficina.observacoes.trim() || undefined,
                    });
                    setNovaOficina({
                      regiaoId: '',
                      nome: '',
                      endereco: '',
                      telefone: '',
                      contato: '',
                      observacoes: '',
                    });
                  },
                  'Oficina criada.',
                  'Não foi possível criar a oficina.',
                )
              }
            >
              Adicionar
            </button>
          </div>

          {/* Lista longa pede busca e paginação (regra de 16/09/2026). */}
          <div className="admin-form-grid" style={{ marginTop: 20 }}>
            <div className="admin-input-group" style={{ maxWidth: 360 }}>
              <input
                className="admin-input"
                value={buscaOficina}
                onChange={(e) => {
                  setBuscaOficina(e.target.value);
                  setPaginaOficinas(1);
                }}
                placeholder="Buscar oficina ou região"
                aria-label="Buscar oficina"
              />
            </div>
          </div>

          <div style={{ marginTop: 20 }}>
            <TabelaSistema
              colunas={colunasOficinas}
              itens={oficinasDaPagina}
              chave={(o) => o.id}
              vazio={
                buscaOficina ? 'Nenhuma oficina encontrada.' : 'Nenhuma oficina cadastrada ainda.'
              }
            />
          </div>

          {totalPaginasOficinas > 1 && (
            <Paginacao
              paginaAtual={paginaOficinas}
              totalPaginas={totalPaginasOficinas}
              onMudarPagina={setPaginaOficinas}
            />
          )}
        </div>
      )}

      {aba === 'ETAPAS' && (
        <div className="admin-card">
          <h3 className="admin-card-title">Etapas informadas pela oficina</h3>
          <p className="admin-hint">
            O que a oficina responde quando o inspetor pergunta em que pé está o carro (funilaria,
            pintura, pronto). Não é esteira e não é acompanhamento em tempo real — é o que alguém
            disse no dia da visita.
          </p>

          <div className="admin-form-grid">
            <div className="admin-input-group">
              <label className="admin-label" htmlFor="oe-nova-etapa">
                Nome da etapa
              </label>
              <input
                id="oe-nova-etapa"
                className="admin-input"
                value={novaEtapa}
                onChange={(e) => setNovaEtapa(e.target.value)}
                placeholder="Ex: Funilaria"
              />
            </div>
            <button
              type="button"
              className="admin-btn admin-btn-primary"
              disabled={salvando || !novaEtapa.trim()}
              onClick={() =>
                executar(
                  async () => {
                    await criarEtapaExterna(novaEtapa.trim());
                    setNovaEtapa('');
                  },
                  'Etapa criada.',
                  'Não foi possível criar a etapa.',
                )
              }
            >
              Adicionar
            </button>
          </div>

          <div style={{ marginTop: 20 }}>
            <TabelaSistema
              colunas={colunasEtapas}
              itens={etapas}
              chave={(e) => e.id}
              vazio="Nenhuma etapa cadastrada ainda."
            />
          </div>
        </div>
      )}

      {aba === 'CHECKLIST' && (
        <div className="admin-card">
          <h3 className="admin-card-title">Checklist da visita</h3>
          <p className="admin-hint">
            As perguntas que o inspetor responde em cada visita. A mudança vale para as visitas
            registradas a partir de agora, e chega ao celular dele no próximo download dos dados —
            por isso uma pergunta obrigatória nova não reprova visita preenchida antes disso.
          </p>

          <div className="admin-form-grid">
            <div className="admin-input-group">
              <label className="admin-label" htmlFor="oe-item-rotulo">
                Pergunta
              </label>
              <input
                id="oe-item-rotulo"
                className="admin-input"
                value={novoItem.rotulo}
                onChange={(e) => setNovoItem({ ...novoItem, rotulo: e.target.value })}
                placeholder="Ex: O veículo está coberto?"
              />
            </div>
            <div className="admin-input-group fixed-width">
              <label className="admin-label" htmlFor="oe-item-tipo">
                Tipo da resposta
              </label>
              <select
                id="oe-item-tipo"
                className="admin-select"
                value={novoItem.tipo}
                onChange={(e) =>
                  setNovoItem({ ...novoItem, tipo: e.target.value as ChecklistVisitaTipo })
                }
              >
                {TIPOS.map((t) => (
                  <option key={t.valor} value={t.valor}>
                    {t.rotulo}
                  </option>
                ))}
              </select>
            </div>
            {TIPOS_COM_OPCOES.includes(novoItem.tipo) && (
              <div className="admin-input-group">
                <label className="admin-label" htmlFor="oe-item-opcoes">
                  Opções (separadas por ponto e vírgula, mínimo 2)
                </label>
                <input
                  id="oe-item-opcoes"
                  className="admin-input"
                  value={novoItem.opcoes}
                  onChange={(e) => setNovoItem({ ...novoItem, opcoes: e.target.value })}
                  placeholder="Ex: Sim; Não; Parcialmente"
                />
              </div>
            )}
            <div className="admin-input-group fixed-width">
              <label className="admin-checkbox-label" style={{ minHeight: 42 }}>
                <input
                  type="checkbox"
                  checked={novoItem.obrigatorio}
                  onChange={(e) => setNovoItem({ ...novoItem, obrigatorio: e.target.checked })}
                />
                Resposta obrigatória
              </label>
            </div>
            <button
              type="button"
              className="admin-btn admin-btn-primary"
              disabled={salvando || !novoItem.rotulo.trim()}
              onClick={() =>
                executar(
                  async () => {
                    await criarItemDoChecklist({
                      rotulo: novoItem.rotulo.trim(),
                      tipo: novoItem.tipo,
                      obrigatorio: novoItem.obrigatorio,
                      opcoes: TIPOS_COM_OPCOES.includes(novoItem.tipo)
                        ? novoItem.opcoes
                            .split(';')
                            .map((o) => o.trim())
                            .filter(Boolean)
                        : undefined,
                    });
                    setNovoItem({ rotulo: '', tipo: 'SIM_NAO', obrigatorio: false, opcoes: '' });
                  },
                  'Pergunta criada.',
                  'Não foi possível criar a pergunta.',
                )
              }
            >
              Adicionar
            </button>
          </div>

          <div style={{ marginTop: 20 }}>
            <TabelaSistema
              colunas={colunasChecklist}
              itens={checklist?.itens ?? []}
              chave={(i) => i.id}
              vazio="Nenhuma pergunta cadastrada. O inspetor registra a visita mesmo assim — com etapa, previsão, observações e fotos."
            />
          </div>
        </div>
      )}

      {aba === 'PARAMETROS' && (
        <>
          <div className="admin-card">
            <h3 className="admin-card-title">Dias sem visita</h3>
            <p className="admin-hint">
              A partir de quantos dias sem visita um veículo (e a oficina dele) aparece sinalizado
              na cobertura e gera aviso ao Chefe de Oficina, ao setor de Eventos e aos
              administradores, às 8h. O aviso sai uma vez por veículo, até a próxima visita.
            </p>

            <div className="admin-form-grid">
              <div className="admin-input-group fixed-width">
                <label className="admin-label" htmlFor="oe-dias-alerta">
                  Dias sem visita para sinalizar
                </label>
                <input
                  id="oe-dias-alerta"
                  className="admin-input"
                  type="number"
                  min={1}
                  max={60}
                  value={diasAlerta}
                  onChange={(e) => setDiasAlerta(Number(e.target.value))}
                />
              </div>
              <button
                type="button"
                className="admin-btn admin-btn-primary"
                disabled={salvando || diasAlerta < 1 || diasAlerta > 60}
                onClick={() =>
                  executar(
                    () => salvarParametrosOficinaExterna(diasAlerta),
                    'Parâmetro salvo.',
                    'Não foi possível salvar o parâmetro.',
                  )
                }
              >
                Salvar
              </button>
              <button
                type="button"
                className="admin-btn admin-btn-secondary"
                disabled={salvando}
                title="Envia agora o aviso das 8h, só para os veículos que ainda não foram avisados"
                onClick={avisarAgora}
              >
                Avisar agora
              </button>
            </div>

            {parametros?.padraoDoSistema ? (
              <p className="admin-texto-secundario">
                Nunca foi alterado — o sistema está usando o padrão de{' '}
                {parametros.diasSemVisitaAlerta} dias.
              </p>
            ) : (
              parametros?.alteradoPor && (
                <p className="admin-texto-secundario">
                  Última alteração por{' '}
                  {parametros.alteradoPor.fullName || parametros.alteradoPor.username} em{' '}
                  {parametros.alteradoEm
                    ? new Date(parametros.alteradoEm).toLocaleString('pt-BR')
                    : '-'}
                  .
                </p>
              )
            )}
          </div>

          <div className="admin-card">
            <h3 className="admin-card-title">Histórico de alterações</h3>
            <p className="admin-hint">
              Quem alterou o quê nos cadastros da Oficina Externa, e quando.
            </p>
            <button
              type="button"
              className="admin-btn admin-btn-secondary"
              onClick={() => setHistoricoAberto(true)}
            >
              Ver histórico
            </button>
          </div>
        </>
      )}

      <HistoricoAlteracoes
        isOpen={historicoAberto}
        onClose={() => setHistoricoAberto(false)}
        entity="oficina_externa"
        titulo="Histórico - Oficina Externa"
        descricao={
          'Alterações nos cadastros (regiões, oficinas, etapas, checklist da visita e o limiar ' +
          'de dias sem visita) e as duas ações de estado do inspetor: cadastro do veículo na ' +
          'oficina e registro de saída. As visitas em si não entram aqui — cada uma já guarda ' +
          'quem a fez e quando, e não pode ser alterada.'
        }
        rotuloAlvo="Cadastro"
      />

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Oficina Externa"
        secoes={helpSections}
      />
    </div>
  );
};

export default OficinaExternaConfigPage;
